// Import the new shared interfaces from the backend models
import type { Panel, ChatMessage } from "../../../src/models/data-models";
import type { LLMModel } from "../../../src/models/llmModels";
// Re-export the LLMModel type so frontend components can import it from this module
export type { LLMModel };
import { useAuth } from "@/composables/useAuth";

// Define the type for the data needed to create a panel configuration
// Based on Panel, excluding server-generated fields
type CreatePanelData = Pick<
  Panel,
  "title" | "instructions" | "agentType" | "settings" | "schedule"
>;

// Define the user record response type
interface UserRecordResponse {
  user: {
    id: string;
    name: string;
    email: string;
    panelSequence: string[];
    settings: Record<string, unknown>;
    lastLogin?: string;
    lastUpdate?: string;
  };
  message: string;
}

// Reduced session shape returned by new establishUserSession endpoint
interface UserSessionResponse {
  name: string | null;
  email: string | null;
  isAuthorized: boolean;
  settings: Record<string, unknown>;
}

// Use environment variable for API base URL, default to Azure production if not set
const API_BASE_URL =
  process.env.VUE_APP_API_BASE_URL || "https://agentpanels.azurewebsites.net/api";

// Store ETags for content endpoints
const contentEtags: { [panelId: string]: string } = {};

/**
 * Fetches the list of panel configurations for the authenticated user.
 * Returns an array of Panel objects.
 */
export async function fetchPanels(): Promise<Panel[]> {
  const { getApiToken } = useAuth();
  console.log("API: fetchPanelConfigs called");
  const token = await getApiToken();
  console.log("API: Token received, fetching panel configs...");

  const response = await fetch(`${API_BASE_URL}/panels`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  console.log("API: fetchPanelConfigs response status:", response.status);

  if (!response.ok) {
    let errorDetails = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorDetails += `, Message: ${JSON.stringify(errorBody)}`;
    } catch (e) {
      /* ignore */
    }
    console.error("API: Fetch panel configs failed (HTTP error):", errorDetails);
    throw new Error(errorDetails);
  }

  const responseJson = await response.json();
  console.log("API: fetchPanelConfigs received:", responseJson);
  if (responseJson && Array.isArray(responseJson.items)) {
    console.log("API: Panel configs fetched successfully.");
    return responseJson.items as Panel[];
  } else {
    console.error("API: Invalid response structure received:", responseJson);
    throw new Error("Invalid API response structure: 'items' array not found.");
  }
}

/**
 * Fetches a single panel configuration by its ID.
 */
export async function fetchPanelById(id: string): Promise<Panel> {
  const { getApiToken } = useAuth();
  console.log("API: fetchPanelConfigById called with id:", id);
  const token = await getApiToken();
  const response = await fetch(`${API_BASE_URL}/panels/${id}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  console.log("API: fetchPanelConfigById response status:", response.status);
  if (!response.ok) {
    throw new Error(`Failed to fetch panel config ${id}: ${response.status}`);
  }
  const data = await response.json();
  console.log("API: fetchPanelConfigById received:", data);
  return data as Panel;
}

/**
 * Creates a new panel configuration for the authenticated user.
 * @param panelConfigData - The data for the new panel config (title, instructions, etc.)
 * @returns The newly created Panel object from the API.
 */
export async function createPanel(panelConfigData: CreatePanelData): Promise<Panel> {
  const { getApiToken } = useAuth();
  console.log("API: createPanelConfigApi called with data:", panelConfigData);
  const token = await getApiToken();
  console.log("API: Token received, creating panel config...");

  // Ensure instructions is included, adjust based on Panel definition
  const payload = {
    title: panelConfigData.title,
    instructions: panelConfigData.instructions,
    agentType: panelConfigData.agentType,
    settings: panelConfigData.settings,
    schedule: panelConfigData.schedule,
  };

  const response = await fetch(`${API_BASE_URL}/panels`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  console.log("API: createPanelConfigApi response status:", response.status);

  if (!response.ok) {
    let errorDetails = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorDetails += `, Message: ${JSON.stringify(errorBody)}`;
    } catch (e) {
      /* ignore */
    }
    console.error("API: Create panel config failed (HTTP error):", errorDetails);
    throw new Error(errorDetails);
  }

  const newPanelConfig = await response.json();
  console.log("API: createPanelConfigApi received:", newPanelConfig);
  console.log("API: Panel config created successfully:", newPanelConfig);
  return newPanelConfig as Panel;
}

/**
 * Updates an existing panel configuration.
 */
export async function patchPanel(id: string, panelData: Panel): Promise<Panel> {
  const { getApiToken } = useAuth();
  const token = await getApiToken();
  const response = await fetch(`${API_BASE_URL}/panels/${id}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(panelData),
  });

  if (!response.ok) {
    throw new Error(`Failed to update panel ${id}: ${response.status}`);
  }

  const updated = await response.json();
  return updated as Panel;
}

/**
 * Fetches the content for a specific panel.
 * Handles ETag caching.
 * @returns The content string, or null if content hasn't changed (304 Not Modified).
 */
export async function fetchPanelContent(id: string): Promise<string | null> {
  const { getApiToken } = useAuth();
  console.log("API: fetchPanelContent called with id:", id);
  const token = await getApiToken();
  const headers: HeadersInit = {
    Authorization: `Bearer ${token}`,
    Accept: "text/markdown", // Request markdown
  };

  // Add If-None-Match header if we have a stored ETag for this panel
  if (contentEtags[id]) {
    headers["If-None-Match"] = contentEtags[id];
  }

  const response = await fetch(`${API_BASE_URL}/panels/${id}/content`, {
    method: "GET",
    headers: headers,
  });

  console.log("API: fetchPanelContent response status:", response.status);

  if (response.status === 304) {
    console.log(`API: Content for panel ${id} not modified (ETag: ${contentEtags[id]})`);
    return null; // Indicate content hasn't changed
  }

  if (!response.ok) {
    // Clear ETag on error other than 304
    delete contentEtags[id];
    throw new Error(`Failed to fetch panel content ${id}: ${response.status}`);
  }

  // Store the new ETag from the response header
  const newEtag = response.headers.get("ETag");
  if (newEtag) {
    contentEtags[id] = newEtag;
    console.log(`API: Updated ETag for panel ${id} content: ${newEtag}`);
  }

  const content = await response.text();
  console.log("API: fetchPanelContent received:", content);
  return content;
}

/**
 * Triggers the background refresh process for a panel's content.
 */
export async function triggerPanelContentRefresh(id: string): Promise<void> {
  const { getApiToken } = useAuth();
  console.log("API: triggerPanelContentRefresh called with id:", id);
  const token = await getApiToken();
  const response = await fetch(`${API_BASE_URL}/panels/${id}/content/refresh`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  console.log("API: triggerPanelContentRefresh response status:", response.status);

  if (!response.ok && response.status !== 202) {
    // Allow 202 Accepted
    throw new Error(`Failed to trigger panel content refresh ${id}: ${response.status}`);
  }
  console.log(`API: Triggered content refresh for panel ${id}`);
}

/**
 * Fetches chat messages for a specific panel.
 */
export async function fetchChatMessages(id: string): Promise<ChatMessage[]> {
  const { getApiToken } = useAuth();
  console.log("API: fetchChatMessages called with id:", id);
  const token = await getApiToken();
  const response = await fetch(`${API_BASE_URL}/panels/${id}/chat/messages`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  console.log("API: fetchChatMessages response status:", response.status);
  if (!response.ok) {
    throw new Error(`Failed to fetch chat messages for panel ${id}: ${response.status}`);
  }
  const data = await response.json();
  console.log("API: fetchChatMessages received:", data);
  if (data && Array.isArray(data.items)) {
    return data.items as ChatMessage[];
  }
  throw new Error("Invalid response structure for chat messages");
}

/**
 * Posts a new chat message to a panel and gets the LLM reply.
 */
export async function postChatMessage(id: string, message: string): Promise<ChatMessage> {
  const { getApiToken } = useAuth();
  console.log("API: postChatMessage called with id:", id, "message:", message);
  const token = await getApiToken();
  const response = await fetch(`${API_BASE_URL}/panels/${id}/chat/messages`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ message: message }),
  });
  console.log("API: postChatMessage response status:", response.status);
  if (!response.ok) {
    throw new Error(`Failed to post chat message for panel ${id}: ${response.status}`);
  }
  const data = await response.json();
  console.log("API: postChatMessage received:", data);
  return data as ChatMessage; // Expecting the LLM reply message
}

/**
 * Fetches the status of the backend service.
 * Returns the status string (e.g., 'ok', 'error', etc.)
 */
export async function fetchStatus(): Promise<string> {
  console.log("API: fetchStatus called");
  const response = await fetch(`${API_BASE_URL}/status`);
  console.log("API: fetchStatus response status:", response.status);
  if (!response.ok) {
    throw new Error(`Failed to fetch status: ${response.status}`);
  }
  const data = await response.json();
  console.log("API: fetchStatus received:", data);
  return data.status || "ok";
}

/**
 * Deletes a panel by its ID.
 */
export async function deletePanel(id: string): Promise<void> {
  const { getApiToken } = useAuth();
  const token = await getApiToken();
  const response = await fetch(`${API_BASE_URL}/panels/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to delete panel ${id}: ${response.status}`);
  }
}

/**
 * Creates or updates user record after login.
 * Uses the provided API token from Authorization header.
 */
export async function createOrUpdateUserRecord(apiToken: string, userData: { name?: string } = {}): Promise<UserRecordResponse> {
  console.log("API: createOrUpdateUserRecord called");
  console.log("API: Token received, creating/updating user record...");

  const response = await fetch(`${API_BASE_URL}/user`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${apiToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(userData),
  });

  console.log("API: createOrUpdateUserRecord response status:", response.status);

  if (!response.ok) {
    let errorDetails = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorDetails += `, Message: ${JSON.stringify(errorBody)}`;
    } catch (e) {
      /* ignore */
    }
    console.error("API: Create/update user record failed (HTTP error):", errorDetails);
    throw new Error(errorDetails);
  }

  const result = await response.json();
  console.log("API: createOrUpdateUserRecord successful:", result);
  return result;
}

/**
 * Establish a reduced user session for the SPA. Returns only safe fields.
 */
export async function establishUserSession(apiToken: string, userData: { name?: string } = {}): Promise<UserSessionResponse> {
  console.log("API: establishUserSession called");
  const response = await fetch(`${API_BASE_URL}/user/session`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${apiToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(userData),
  });

  console.log("API: establishUserSession response status:", response.status);
  if (!response.ok) {
    let errorDetails = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorDetails += `, Message: ${JSON.stringify(errorBody)}`;
    } catch (e) {
      /* ignore */
    }
    console.error("API: establishUserSession failed:", errorDetails);
    throw new Error(errorDetails);
  }

  const result = await response.json();
  console.log("API: establishUserSession successful:", result);
  return result as UserSessionResponse;
}

/**
 * Sets up user session with the backend.
 * Uses the provided API token from Authorization header for OBO flow.
 */
export async function enableGraphAccess(apiToken: string): Promise<{ success: boolean }> {
  console.log("API: setupUserSession called");
  console.log("API: Token received, setting up user session...");

  const response = await fetch(`${API_BASE_URL}/user/enableGraphAccess`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${apiToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}), // Empty body - token is in Authorization header
  });

  console.log("API: setupUserSession response status:", response.status);

  if (!response.ok) {
    let errorDetails = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorDetails += `, Message: ${JSON.stringify(errorBody)}`;
    } catch (e) {
      /* ignore */
    }
    console.error("API: Setup user session failed (HTTP error):", errorDetails);
    throw new Error(errorDetails);
  }

  const result = await response.json();
  console.log("API: setupUserSession successful:", result);
  return result;
}

/**
 * Fetches the authentication config (clientId, tenantId, etc) from the backend.
 * Returns the config object as-is from /api/config.
 */
export async function fetchConfig(): Promise<{ clientId: string; tenantId: string;[key: string]: unknown }> {
  // Fetch the remote runtime config (usually from the functions host)
  const response = await fetch(`${API_BASE_URL}/config`);
  if (!response.ok) {
    throw new Error(`Failed to fetch config: ${response.status}`);
  }

  const config = await response.json();
  console.log("fetchConfig: remote clientId=", config.clientId, "tenantId=", config.tenantId);

  // Preserve local overrides when present. This allows developers to override
  // the SPA client id or API base URL locally without the remote config clobbering them.
  const localSpaClientId = process.env.VUE_APP_AZURE_CLIENT_ID || process.env.AZURE_SPA_CLIENT_ID;
  if (localSpaClientId) {
    console.log("fetchConfig: applying local override for spaClientId from env");
    // Put the local value into spaClientId (preferred for browser MSAL)
    config.spaClientId = localSpaClientId;
  }

  // Expose which API_BASE_URL the client is using (may be overridden by local env)
  // This keeps the frontend aware of its effective API base.
  config.apiBaseUrl = API_BASE_URL;

  console.log("fetchConfig: effective spaClientId=", config.spaClientId, "apiBaseUrl=", config.apiBaseUrl);
  return config;
}

/**
 * Fetches the list of available LLM models from the backend.
 * Returns an array of LLMModel objects.
 */
export async function fetchModels(): Promise<LLMModel[]> {
  console.log("API: fetchModels called");
  const response = await fetch(`${API_BASE_URL}/models`);

  console.log("API: fetchModels response status:", response.status);

  if (!response.ok) {
    let errorDetails = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorDetails += `, Message: ${JSON.stringify(errorBody)}`;
    } catch (e) {
      /* ignore */
    }
    console.error("API: Fetch models failed (HTTP error):", errorDetails);
    throw new Error(errorDetails);
  }

  const responseJson = await response.json();
  console.log("API: fetchModels received:", responseJson);
  if (responseJson && Array.isArray(responseJson.items)) {
    console.log("API: Models fetched successfully.");
    return responseJson.items as LLMModel[];
  } else {
    console.error("API: Invalid response structure received:", responseJson);
    throw new Error("Invalid API response structure: 'items' array not found.");
  }
}

